import { API_URL } from "env";
import { mixLogOut } from '../services/mixin';

export interface CustomerTagItem {
    id: number;
    name: string;
    color: string;
    description: string;
    usage_count: number;
    created_by_id: number;
    created_at: string;
    updated_at: string;
    code?: string;
    position?: number;
    text?: string | null;
    group?: string | null;
    /** Các thẻ được phép chuyển sang từ thẻ này (nguồn của `next_tag` trên lịch hẹn) */
    next_ids?: number[] | null;
    /** Trạng thái lịch hẹn tương ứng với thẻ (0..4) */
    status?: number | null;
    status_text?: string | null;
    /** Màn hình lịch hẹn thuộc về: 1 = lịch hẹn, 2 = lịch tư vấn, 3 = lịch thực hiện */
    page?: number | null;
    creator?: {
        email: string;
        fullname: string;
    };
}

export interface CustomerTagListResponse {
    status: boolean;
    data: {
        total: number;
        current_page: number;
        items: CustomerTagItem[];
    } | null;
}

export interface CustomerTagSummaryItem {
    id: number;
    name: string;
    color: string;
    count?: number;
    customers_count?: number;
}

export interface CustomerTagSummaryResponse {
    status: boolean;
    data: CustomerTagSummaryItem[] | {
        total_all?: number;
        items?: CustomerTagSummaryItem[];
    };
    msg?: string;
}

export async function getCustomerTags(params?: {
    search?: string;
    group?: string;
    page?: number;
    sort?: string[];
}): Promise<CustomerTagListResponse> {
    try {
        const token = localStorage.getItem('token') || '';
        const query = new URLSearchParams();
        if (params?.search) query.append('search', params.search);
        if (params?.group) query.append('group', params.group);
        if (params?.page) query.append('page', String(params.page));
        if (params?.sort && params.sort.length > 0) {
            params.sort.forEach(s => query.append('sort', s));
        }
        const queryString = query.toString();

        const response = await fetch(`${API_URL}/api/v1/be/customer-tags${queryString ? `?${queryString}` : ''}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json() as CustomerTagListResponse;
    } catch (error) {
        console.error('Failed to fetch customer tags:', error);
        return {
            status: false,
            data: null,
        };
    }
}

/**
 * Bộ lọc của /customer-tags/summary trùng với /customers, để số đếm trên chip
 * khớp với danh sách bên dưới. Thiếu param nào là chip đếm sai bấy nhiêu.
 */
export interface CustomerTagSummaryParams {
    group?: string;
    search?: string;
    /** Nhân viên phụ trách */
    in_charge_staff_id?: number;
    /** Nhân viên telesale phụ trách */
    contact_staff_id?: number;
    customer_care_status_ids?: number[];
    customer_care_status_id?: number;
    source?: string | string[];
    channel_connection_id?: number[];
    oauth_account_id?: number[];
    start_date?: string;
    end_date?: string;
}

export async function getCustomerTagsSummary(params?: CustomerTagSummaryParams): Promise<CustomerTagSummaryResponse> {
    try {
        const token = localStorage.getItem('token') || '';
        const query = new URLSearchParams();

        const appendMaybe = (key: string, value?: string | number) => {
            if (value !== undefined && value !== null && value !== '') query.append(key, String(value));
        };
        const appendEach = (key: string, values?: (string | number)[]) => {
            values?.forEach((v) => query.append(key, String(v)));
        };

        appendMaybe('group', params?.group);
        appendMaybe('search', params?.search);
        appendMaybe('in_charge_staff_id', params?.in_charge_staff_id);
        appendMaybe('contact_staff_id', params?.contact_staff_id);
        appendMaybe('customer_care_status_id', params?.customer_care_status_id);
        appendMaybe('start_date', params?.start_date);
        appendMaybe('end_date', params?.end_date);
        appendEach('customer_care_status_ids', params?.customer_care_status_ids);
        appendEach('channel_connection_id', params?.channel_connection_id);
        appendEach('oauth_account_id', params?.oauth_account_id);
        appendEach('source', Array.isArray(params?.source) ? params?.source : params?.source ? [params.source] : undefined);

        const queryString = query.toString();

        const response = await fetch(`${API_URL}/api/v1/be/customer-tags/summary${queryString ? `?${queryString}` : ''}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json() as CustomerTagSummaryResponse;
    } catch (error) {
        console.error('Failed to fetch customer tag summary:', error);
        return {
            status: false,
            data: { total_all: 0, items: [] },
        };
    }
}
